import type { CreateAccountState } from "@/hooks/accounts/useCreateAccountStore";
import type { CreateInsuranceFormData } from "@/types/insurance/insurance";
import type { CreateParameterFormData } from "@/types/parameter/parameter";
import {
	AnnualSalaryAboveXGInput,
	// Salary inputs
	AnnualSalaryInput,
	BaseCompensationDependentsSupplementInput,
	// Compensation inputs
	BaseCompensationInput,
	ChildSupplementInput,
	ChildSupplementCoordinationInput,
	CoordinationAmountInput,
	DependentsSupplementInput,
	EndAgeInput,
	FixedDependentsAllowanceInput,
	FixedDependentsSupplementInput,
	// Misc inputs
	ForNumberOfYearsInput,
	FuneralAllowanceInput,
	IfNonProviderCompensationInput,
	IncreaseBefore34Input,
	IncreaseInput,
	// Intervals input
	IntervalsInput,
	MaxAgeChildInput,
	// Age inputs
	MaxAgeInput,
	MaxCompensationInput,
	MinAgeInput,
	MinCompensationInput,
	MinInvalidityInput,
	ReductionInGInput,
	// Percentage inputs
	ReductionInput,
	StartAgeInput,
	CompensationTaperStartAgeInput,
} from "./ParameterInputs";
import { getInputClass } from "./ParameterInputs/utils";

interface ParameterInputProps {
	store: CreateAccountState;
	insurance: CreateInsuranceFormData;
	parameter: CreateParameterFormData;
	alwaysExpanded?: boolean;
}

export const ParameterInput = ({
	store,
	insurance,
	parameter,
	alwaysExpanded,
}: ParameterInputProps) => {
	const rest = {
		store,
		insurance,
		getInputClass: () => getInputClass(parameter),
		alwaysExpanded,
	};

	switch (parameter.type) {
		case "base-compensation":
			return <BaseCompensationInput parameter={parameter} {...rest} />;
		case "annual-salary":
			return <AnnualSalaryInput parameter={parameter} {...rest} />;
		case "reduction":
			return <ReductionInput parameter={parameter} {...rest} />;
		case "increase":
			return <IncreaseInput parameter={parameter} {...rest} />;
		case "increase-before-34":
			return <IncreaseBefore34Input parameter={parameter} {...rest} />;
		case "annual-salary-above-x-g":
			return <AnnualSalaryAboveXGInput parameter={parameter} {...rest} />;
		case "intervals":
			return <IntervalsInput parameter={parameter} {...rest} />;
		case "dependents-supplement":
			return <DependentsSupplementInput parameter={parameter} {...rest} />;
		case "fixed-dependents-supplement":
			return <FixedDependentsSupplementInput parameter={parameter} {...rest} />;
		case "fixed-dependents-allowance":
			return <FixedDependentsAllowanceInput parameter={parameter} {...rest} />;
		case "child-supplement":
			return <ChildSupplementInput parameter={parameter} {...rest} />;
		case "funeral-allowance":
			return <FuneralAllowanceInput parameter={parameter} {...rest} />;
		case "max-age":
			return <MaxAgeInput parameter={parameter} {...rest} />;
		case "min-age":
			return <MinAgeInput parameter={parameter} {...rest} />;
		case "max-compensation":
			return <MaxCompensationInput parameter={parameter} {...rest} />;
		case "min-compensation":
			return <MinCompensationInput parameter={parameter} {...rest} />;
		case "start-age":
			return <StartAgeInput parameter={parameter} {...rest} />;
		case "compensation-taper-start-age":
			return <CompensationTaperStartAgeInput parameter={parameter} {...rest} />;
		case "end-age":
			return <EndAgeInput parameter={parameter} {...rest} />;
		case "for-number-of-years":
			return <ForNumberOfYearsInput parameter={parameter} {...rest} />;
		case "reduction-in-g":
			return <ReductionInGInput parameter={parameter} {...rest} />;
		case "if-non-provider-compensation":
			return <IfNonProviderCompensationInput parameter={parameter} {...rest} />;
		case "base-compensation-child-supplement":
			return null;
		case "base-compensation-dependents-supplement":
			if (
				insurance.type === "leisure-accident-death" ||
				insurance.type === "extended-wc-death" ||
				insurance.type === "trygghetsforsikring"
			) {
				return null;
			}
			return (
				<BaseCompensationDependentsSupplementInput
					parameter={parameter}
					{...rest}
				/>
			);
	case "max-age-child-supplement":
	case "min-age-child-supplement":
		// Rendered inside ChildSupplementInput card
		return null;
	case "max-age-dependents-supplement":
	case "min-age-dependents-supplement":
		// Rendered inside DependentsSupplementInput card
		return null;
		case "max-age-child":
			return <MaxAgeChildInput parameter={parameter} {...rest} />;
		case "coordination-amount":
			return <CoordinationAmountInput parameter={parameter} {...rest} />;
		case "min-invalidity":
			return <MinInvalidityInput parameter={parameter} {...rest} />;
		case "child-supplement-coordination":
			return (
				<ChildSupplementCoordinationInput parameter={parameter} {...rest} />
			);
	case "dependents-supplement-age-reduction-rate":
	case "dependents-allowance-taper-start-age":
	case "dependents-allowance-taper-unit":
	case "dependents-allowance-taper-1-end-age":
	case "dependents-allowance-taper-2-start-age":
	case "dependents-allowance-taper-2-unit":
	case "dependents-allowance-taper-2-rate":
	case "dependents-allowance-taper-2-end-age":
		// Rendered inline inside FixedDependentsAllowanceInput
		return null;
	default:
		return null;
	}
};
